import { Injectable, inject, signal, effect, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';

const API = environment.apiUrl;

/** One item in the header bell (dbo.Notifications). */
export interface InboxItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  action_url: string | null;
  is_read: boolean;
  created_date_time: string;
}

/** How often the bell checks for new notifications while signed in. */
const POLL_MS = 60_000;

/**
 * The header bell's in-app notifications (task assigned / tagged / status changed…). Named "inbox"
 * to keep it apart from NotificationService, which shows toasts.
 *
 * Polls while signed in, and refreshes when the tab regains focus, so a new notification shows up
 * within a minute without a page reload.
 */
@Injectable({ providedIn: 'root' })
export class InboxService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  readonly items = signal<InboxItem[]>([]);
  readonly unreadCount = signal(0);

  private timer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Start/stop with the session.
    effect(() => {
      const signedIn = this.auth.isAuthenticated();
      untracked(() => signedIn ? this.start() : this.stop());
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => { if (this.timer) this.refresh(); });
    }
  }

  private start() {
    if (this.timer) return;
    this.refresh();
    this.timer = setInterval(() => this.refresh(), POLL_MS);
  }

  private stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.items.set([]);
    this.unreadCount.set(0);
  }

  async refresh(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ items: InboxItem[]; unread_count: number }>(`${API}/api/notifications`, { params: { take: 20 } }));
      this.items.set(res.items);
      this.unreadCount.set(res.unread_count);
    } catch {
      // The bell is not worth an error toast; it simply tries again on the next poll.
    }
  }

  async markRead(item: InboxItem): Promise<void> {
    if (item.is_read) return;
    this.items.update(list => list.map(n => n.id === item.id ? { ...n, is_read: true } : n));
    this.unreadCount.update(c => Math.max(0, c - 1));
    try { await firstValueFrom(this.http.post<void>(`${API}/api/notifications/${item.id}/read`, {})); }
    catch { this.refresh(); }
  }

  async markAllRead(): Promise<void> {
    this.items.update(list => list.map(n => ({ ...n, isRead: true })));
    this.unreadCount.set(0);
    try { await firstValueFrom(this.http.post<void>(`${API}/api/notifications/read-all`, {})); }
    catch { this.refresh(); }
  }
}
